/**
 * Scheduling math for the Inspections Scheduler.
 *
 * The re-inspection backend is rolling (weeklyInspectionReset creates a new
 * inspection N days after the last completed one). On TOP of that, the
 * scheduler frames a Monday-anchored weekly window and tightens the deadline
 * when an owner is arriving. All day-granularity; "now" drives the window.
 */
import moment from "moment";

// Monday-anchored (ISO) week containing `m`.
export const weekStart = (m = moment()) => m.clone().startOf("isoWeek"); // Monday 00:00
export const weekEnd = (m = moment()) => m.clone().endOf("isoWeek"); //   Sunday 23:59

// "Open" = still actionable (not completed / cancelled / missed). Robust to the
// legacy status-string drift ("in_progress" / "in-progress" / "in progress").
export const isOpenInspection = (insp) => {
  const s = String(insp?.status || "").toLowerCase();
  return (
    !s.includes("complete") && !s.includes("cancel") && !s.includes("miss")
  );
};

/**
 * Effective deadline for an OPEN inspection: the earliest of
 *  - its explicit `deadline` (if set),
 *  - the end of the current Mon–Sun week,
 *  - `ownerArrivalDate` − 1 day (the inspection must finish before arrival).
 * Returns a moment, or null for completed/cancelled.
 */
export const effectiveDeadline = (insp, now = moment()) => {
  if (!insp || !isOpenInspection(insp)) return null;
  // Base = the explicit deadline if one was set, else the end of this week
  // (the default weekly cadence). Owner arrival can only tighten it, never
  // extend it.
  let base = weekEnd(now);
  if (insp.deadline) {
    const d = moment(insp.deadline);
    // The stored deadline is a precise end-of-window instant (computed in the
    // property's own timezone by weeklyInspectionReset), so use it as-is — do
    // NOT re-shift to the viewer's local end-of-day or it drifts across zones.
    if (d.isValid()) base = d.clone();
  }
  const candidates = [base];
  if (insp.ownerArrivalDate) {
    const a = moment(insp.ownerArrivalDate);
    if (a.isValid()) candidates.push(a.clone().subtract(1, "day").endOf("day"));
  }
  return moment.min(candidates);
};

export const isOverdue = (insp, now = moment()) => {
  const d = effectiveDeadline(insp, now);
  return d ? d.isBefore(now) : false;
};

// Days-before-deadline that inspectionDeadlineReminders pushes on. Mirror of
// DEFAULT_THRESHOLDS in functions/lib/deadlineReminders.js — keep the two in
// sync. (No 0: the backend doesn't fire a distinct deadline-day push, so
// advertising one here would promise a reminder that never sends.)
export const REMINDER_THRESHOLDS = [5, 3, 1];

/**
 * The next upcoming automatic inspection-reminder instant for an OPEN
 * inspection, or null if none remain (deadline passed). Reminders fire on
 * deadline−5/−3/−1 days and on the deadline day; the next is the soonest of
 * those that is today-or-later. Pure date math off the (effective) deadline, so
 * it matches the cron without needing the per-inspection sent markers.
 */
export const nextInspectionReminder = (insp, now = moment()) => {
  const dl = effectiveDeadline(insp, now);
  if (!dl) return null;
  const today = now.clone().startOf("day");
  const deadlineDay = dl.clone().startOf("day");
  const dates = REMINDER_THRESHOLDS.map((t) =>
    deadlineDay.clone().subtract(t, "day"),
  )
    .filter((d) => d.isSameOrAfter(today, "day"))
    .sort((a, b) => a.valueOf() - b.valueOf());
  return dates.length ? dates[0] : null;
};

// ---- owner visits (date ranges on property.ownerVisits) --------------------

export const normalizeVisits = (property) =>
  Array.isArray(property?.ownerVisits) ? property.ownerVisits : [];

/** The current (in-progress) or next upcoming owner visit, or null. */
export const nextOwnerVisit = (property, now = moment()) => {
  const visits = normalizeVisits(property)
    .map((v) => ({
      ...v,
      _start: moment(v.startDate),
      _end:
        v.endDate && moment(v.endDate).isValid()
          ? moment(v.endDate)
          : moment(v.startDate),
    }))
    .filter((v) => v._start.isValid())
    .sort((a, b) => a._start.valueOf() - b._start.valueOf());
  // soonest visit whose end hasn't passed (covers an in-progress visit too)
  return visits.find((v) => v._end.isSameOrAfter(now, "day")) || null;
};

/** Does `day` (a moment) fall inside any of the property's owner-visit ranges? */
export const dayHasOwnerVisit = (property, day) =>
  normalizeVisits(property).some((v) => {
    const s = moment(v.startDate);
    const e = v.endDate && moment(v.endDate).isValid() ? moment(v.endDate) : s;
    if (!s.isValid()) return false;
    return day.isBetween(s.clone().startOf("day"), e.clone().endOf("day"), "day", "[]");
  });
